import type { ColumnDeletePlan } from './planCapTableColumnDeletes';

export interface BatchDeleteParsedColumn {
  order?: number | null;
  name?: string | null;
  is_deleted?: boolean | null;
  cascade_delete?: boolean;
  security_type?: number;
  security_ref?: string | null;
  convertible_notes?: Array<{ security_group_ref?: string | null }> | null;
}

export interface BatchDeleteRelatedSecurity {
  security_ref?: string | null;
}

const columnKey = (column: { security_ref?: string | null; order?: number | null }): string =>
  column.security_ref ? `ref:${column.security_ref}` : `order:${column.order}`;

/**
 * Mark the planned securities deleted on an already reverse-parsed column list.
 * Lookup is always `.order === order`, never array position.
 */
export const applyCapTableBatchDeletes = <T extends BatchDeleteParsedColumn>(
  parsedColumns: T[],
  plan: ColumnDeletePlan,
  relatedSecurities: BatchDeleteRelatedSecurity[] = []
): { columns: T[]; newlyDeleted: BatchDeleteParsedColumn[] } => {
  const cascade = new Set(plan.cascadeOrders);
  const newlyDeleted: BatchDeleteParsedColumn[] = [];
  const seen = new Set<string>();
  const pushDeleted = (column: BatchDeleteParsedColumn) => {
    const key = columnKey(column);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    newlyDeleted.push(column);
  };

  plan.deletableOrders.forEach(order => {
    const column: BatchDeleteParsedColumn | undefined = parsedColumns.find(candidate => candidate.order === order);
    if (!column) {
      return;
    }
    column.is_deleted = true;
    if (cascade.has(order)) {
      column.cascade_delete = true;
    }
    pushDeleted(column);
    if (column.security_type !== 6) {
      return;
    }
    const relatedRefs = new Set(
      (column.convertible_notes ?? []).map(note => note.security_group_ref).filter(Boolean) as string[]
    );
    relatedSecurities.forEach(security => {
      if (!security.security_ref || !relatedRefs.has(security.security_ref)) {
        return;
      }
      const hidden: BatchDeleteParsedColumn = {
        ...security,
        is_deleted: true,
        ...(cascade.has(order) ? { cascade_delete: true } : {}),
      };
      pushDeleted(hidden);
    });
  });

  return { columns: parsedColumns, newlyDeleted };
};

/** Append newly deleted columns without dropping ones already queued. */
export const mergePersistentDeletedColumns = <T extends { security_ref?: string | null; order?: number | null }>(
  current: T[],
  incoming: T[]
): T[] => {
  const seen = new Set(current.map(columnKey));
  const extras = incoming.filter(column => {
    const key = columnKey(column);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
  return [...current, ...extras];
};
